import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminFetch, getAdminUser, clearAdminTokens, isAdminAuthenticated } from '../../utils/auth';
import { getBackendUrl } from '../../utils/api';

// Ant Design imports
import {
  Layout,
  Typography,
  Button,
  Avatar,
  Badge,
  Dropdown,
  Spin,
  Alert,
  Card,
  Tag,
  Space,
  Skeleton,
  Progress,
  Tooltip,
  Row,
  Col,
  Grid,
  Flex,
  Menu,
  theme,
  Breadcrumb,
} from 'antd';
import type { MenuProps } from 'antd';

// Ant Design Icons
import {
  LogoutOutlined,

  TeamOutlined,
  SettingOutlined,
  PieChartOutlined,
  FileTextOutlined,
  SafetyOutlined,
  ReloadOutlined,
  RiseOutlined,
  FallOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ImportOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  BellOutlined,
  HomeOutlined,
  SyncOutlined,
  ApiOutlined,
  HistoryOutlined,
  CodeOutlined,
  BarChartOutlined,
  PlusOutlined,
  CommentOutlined,
} from '@ant-design/icons';

// Page imports
import ClientManagement from './ClientManagement';
import ServiceStatus from './ServiceStatus';
import Analytics from './Analytics';
import Logs from './Logs';
import UserManagement from './UserManagement';
import DataImportManagement from './DataImportManagement';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Extended page types for submenus
type PageType =
  | 'overview'
  | 'services'
  | 'data-import'
  | 'tenants-all'
  | 'tenants-create'
  | 'tenants-pending'
  | 'users-admin'
  | 'users-roles'
  | 'analytics-cache'
  | 'analytics-search'
  | 'analytics-api'
  | 'analytics-errors'
  | 'analytics-performance'
  | 'analytics-feedback'
  | 'analytics-billing'
  | 'logs-events'
  | 'logs-search-history'
  | 'logs-system';

interface PageConfig {
  key: PageType;
  icon: React.ReactNode;
  label: string;
  description: string;
  parent?: 'logs-analytics' | 'tenants' | 'users';
}

const pageConfigs: Record<PageType, PageConfig> = {
  'overview': { key: 'overview', icon: <PieChartOutlined />, label: 'Dashboard', description: 'Overview & KPIs' },
  'services': { key: 'services', icon: <SettingOutlined />, label: 'Services', description: 'Service health' },
  'data-import': { key: 'data-import', icon: <ImportOutlined />, label: 'Data Import', description: 'Bulk imports' },
  'tenants-all': { key: 'tenants-all', icon: <TeamOutlined />, label: 'All Tenants', description: 'View all tenants', parent: 'tenants' },
  'tenants-create': { key: 'tenants-create', icon: <PlusOutlined />, label: 'Create Tenant', description: 'Register new tenant', parent: 'tenants' },
  'tenants-pending': { key: 'tenants-pending', icon: <ClockCircleOutlined />, label: 'Pending Approvals', description: 'Review pending tenants', parent: 'tenants' },
  'users-admin': { key: 'users-admin', icon: <UserOutlined />, label: 'Admin Users', description: 'Manage admin users', parent: 'users' },
  'users-roles': { key: 'users-roles', icon: <SafetyOutlined />, label: 'Roles & Permissions', description: 'View roles', parent: 'users' },
  'analytics-cache': { key: 'analytics-cache', icon: <SyncOutlined />, label: 'Cache Statistics', description: 'Cache performance', parent: 'logs-analytics' },
  'analytics-search': { key: 'analytics-search', icon: <SearchOutlined />, label: 'Search Analytics', description: 'Search trends', parent: 'logs-analytics' },
  'analytics-api': { key: 'analytics-api', icon: <ApiOutlined />, label: 'API Usage', description: 'API statistics', parent: 'logs-analytics' },
  'analytics-errors': { key: 'analytics-errors', icon: <ExclamationCircleOutlined />, label: 'Error Analytics', description: 'Error tracking', parent: 'logs-analytics' },
  'analytics-performance': { key: 'analytics-performance', icon: <ThunderboltOutlined />, label: 'Performance', description: 'Response time metrics', parent: 'logs-analytics' },
  'analytics-feedback': { key: 'analytics-feedback', icon: <CommentOutlined />, label: 'Feedback', description: 'User feedback stats', parent: 'logs-analytics' },
  'analytics-billing': { key: 'analytics-billing', icon: <BarChartOutlined />, label: 'Billing', description: 'API call counts per tenant', parent: 'logs-analytics' },
  'logs-events': { key: 'logs-events', icon: <FileTextOutlined />, label: 'Event Logs', description: 'API event logs', parent: 'logs-analytics' },
  'logs-search-history': { key: 'logs-search-history', icon: <HistoryOutlined />, label: 'Search History', description: 'Search queries', parent: 'logs-analytics' },
  'logs-system': { key: 'logs-system', icon: <CodeOutlined />, label: 'System Logs', description: 'System messages', parent: 'logs-analytics' },
};

function AdminDashboard() {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { token } = theme.useToken();

  const [activePage, setActivePage] = useState<PageType>(() => {
    const saved = sessionStorage.getItem('admin_activePage');
    return (saved as PageType) || 'overview';
  });
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const saved = sessionStorage.getItem('admin_activePage') as PageType;
    const parent = saved ? pageConfigs[saved]?.parent : undefined;
    return parent ? [parent] : [];
  });
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // System health status for sidebar indicator
  const [systemStatus, setSystemStatus] = useState<{
    status: 'success' | 'warning' | 'error' | 'processing';
    text: string;
    healthyCount: number;
    totalCount: number;
  }>({
    status: 'processing',
    text: 'Checking systems...',
    healthyCount: 0,
    totalCount: 0,
  });

  const adminUser = getAdminUser();

  // Persist active page to sessionStorage so browser refresh returns to same page
  useEffect(() => {
    sessionStorage.setItem('admin_activePage', activePage);
  }, [activePage]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  // Fetch system health for sidebar status indicator
  const fetchSystemHealth = async () => {
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/admin/system/health`);
      if (response.ok) {
        const data = await response.json();
        const healthyServices = data.data?.healthy_services || 0;
        const totalServices = data.data?.total_services || 0;
        const healthPercentage = data.data?.health_percentage || 0;

        // Determine status based on health
        let status: 'success' | 'warning' | 'error' = 'success';
        let text = 'All systems operational';

        if (healthPercentage < 100 && healthPercentage >= 70) {
          status = 'warning';
          text = `${totalServices - healthyServices} service(s) degraded`;
        } else if (healthPercentage < 70) {
          status = 'error';
          text = `${totalServices - healthyServices} service(s) down`;
        }

        setSystemStatus({
          status,
          text,
          healthyCount: healthyServices,
          totalCount: totalServices,
        });
      } else {
        setSystemStatus({
          status: 'error',
          text: 'Unable to check status',
          healthyCount: 0,
          totalCount: 0,
        });
      }
    } catch (err) {
      setSystemStatus({
        status: 'error',
        text: 'Connection error',
        healthyCount: 0,
        totalCount: 0,
      });
    }
  };

  // Fetch system health on mount and periodically
  useEffect(() => {
    fetchSystemHealth();
    const interval = setInterval(fetchSystemHealth, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Listen for navigation events from child components (Quick Actions)
  useEffect(() => {
    const handleNavigate = (event: CustomEvent) => {
      if (event.detail) {
        handleMenuClick(event.detail as string);
      }
    };
    window.addEventListener('navigateTo', handleNavigate as EventListener);
    return () => window.removeEventListener('navigateTo', handleNavigate as EventListener);
  }, []);

  const checkAdminAccess = async () => {
    try {
      if (!isAdminAuthenticated()) {
        navigate('/');
        return;
      }
      setLoading(false);
    } catch (err: any) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminFetch(`${getBackendUrl()}/api/auth/admin/logout`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    clearAdminTokens();
    navigate('/console/login');
  };

  const handleMenuClick = (key: string) => {
    setActivePage(key as PageType);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  // Listen for navigateToPage custom events (used by error drill-down)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail === 'string') {
        handleMenuClick(detail);
      }
    };
    window.addEventListener('navigateToPage', handler);
    return () => window.removeEventListener('navigateToPage', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenChange = (keys: string[]) => {
    // Accordion behavior: only one top-level submenu open at a time
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));
    setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
  };

  // Get current page info
  const currentPage = pageConfigs[activePage];

  // Check if user has super_admin role
  const isSuperAdmin = adminUser?.role === 'super_admin';

  // Logs & Analytics submenu items - filter System Logs for non-super_admin
  const logsAnalyticsChildren = [
    {
      key: 'analytics-search',
      icon: <SearchOutlined />,
      label: 'Search Analytics',
      onClick: () => handleMenuClick('analytics-search'),
    },
    {
      key: 'analytics-api',
      icon: <ApiOutlined />,
      label: 'API Usage',
      onClick: () => handleMenuClick('analytics-api'),
    },
    {
      key: 'analytics-errors',
      icon: <ExclamationCircleOutlined />,
      label: 'Error Analytics',
      onClick: () => handleMenuClick('analytics-errors'),
    },
    {
      key: 'analytics-performance',
      icon: <ThunderboltOutlined />,
      label: 'Performance',
      onClick: () => handleMenuClick('analytics-performance'),
    },
    {
      key: 'analytics-cache',
      icon: <SyncOutlined />,
      label: 'Cache Statistics',
      onClick: () => handleMenuClick('analytics-cache'),
    },
    {
      key: 'analytics-billing',
      icon: <BarChartOutlined />,
      label: 'Billing',
      onClick: () => handleMenuClick('analytics-billing'),
    },
    { type: 'divider' as const },
    {
      key: 'logs-search-history',
      icon: <HistoryOutlined />,
      label: 'Search History',
      onClick: () => handleMenuClick('logs-search-history'),
    },
    {
      key: 'logs-events',
      icon: <FileTextOutlined />,
      label: 'Event Logs',
      onClick: () => handleMenuClick('logs-events'),
    },
    // System Logs - only visible to super_admin
    ...(isSuperAdmin ? [{
      key: 'logs-system',
      icon: <CodeOutlined />,
      label: 'System Logs',
      onClick: () => handleMenuClick('logs-system'),
    }] : []),
  ];

  // Sidebar menu items with submenus - Industry standard order:
  // 1. Dashboard (Overview)
  // 2. Tenants (Primary business data)
  // 3. Logs & Analytics (Monitoring/Reporting)
  // 4. Services (Infrastructure)
  // 5. Data Import (Data Management)
  // 6. Users (Administration - typically last, super_admin only)
  const sidebarMenuItems: MenuProps['items'] = [
    {
      key: 'overview',
      icon: <PieChartOutlined />,
      label: 'Dashboard',
      onClick: () => handleMenuClick('overview'),
    },
    { type: 'divider' },
    {
      key: 'tenants',
      icon: <TeamOutlined />,
      label: 'Tenants',
      children: [
        {
          key: 'tenants-all',
          icon: <TeamOutlined />,
          label: 'All Tenants',
          onClick: () => handleMenuClick('tenants-all'),
        },
        {
          key: 'tenants-create',
          icon: <PlusOutlined />,
          label: 'Create Tenant',
          onClick: () => handleMenuClick('tenants-create'),
        },
        {
          key: 'tenants-pending',
          icon: <ClockCircleOutlined />,
          label: 'Pending Approvals',
          onClick: () => handleMenuClick('tenants-pending'),
        },
      ],
    },
    {
      key: 'logs-analytics',
      icon: <BarChartOutlined />,
      label: 'Logs & Analytics',
      children: logsAnalyticsChildren,
    },
    { type: 'divider' },
    {
      key: 'services',
      icon: <SettingOutlined />,
      label: 'Services',
      onClick: () => handleMenuClick('services'),
    },
    {
      key: 'data-import',
      icon: <ImportOutlined />,
      label: 'Data Import',
      onClick: () => handleMenuClick('data-import'),
    },
    // Users menu - only visible to super_admin
    ...(isSuperAdmin ? [
      { type: 'divider' as const },
      {
        key: 'users',
        icon: <SafetyOutlined />,
        label: 'Users',
        children: [
          {
            key: 'users-admin',
            icon: <UserOutlined />,
            label: 'Admin Users',
            onClick: () => handleMenuClick('users-admin'),
          },
          {
            key: 'users-roles',
            icon: <SafetyOutlined />,
            label: 'Roles & Permissions',
            onClick: () => handleMenuClick('users-roles'),
          },
        ],
      },
    ] : []),
  ];

  // User menu items for dropdown
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '8px 0', minWidth: 200 }}>
          <Text strong style={{ display: 'block' }}>{adminUser?.full_name || adminUser?.username}</Text>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{adminUser?.email}</Text>
          <Tag color="blue" style={{ marginTop: 8, textTransform: 'capitalize' }}>
            {adminUser?.role?.replace('_', ' ')}
          </Tag>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign out',
      onClick: handleLogout,
      danger: true,
    },
  ];

  // Get the selected keys for menu highlighting
  const getSelectedKeys = (): string[] => {
    return [activePage];
  };

  // Get default open keys when page is a submenu item
  useEffect(() => {
    const config = pageConfigs[activePage];
    if (config?.parent) {
      setOpenKeys((prev) => {
        if (!prev.includes(config.parent!)) {
          return [...prev, config.parent!];
        }
        return prev;
      });
    }
  }, [activePage]);

  // Render page content
  const renderPageContent = () => {
    switch (activePage) {
      case 'overview':
        return <OverviewTab />;
      case 'tenants-all':
        return <ClientManagement initialTab="all" />;
      case 'tenants-create':
        return <ClientManagement initialTab="create" />;
      case 'tenants-pending':
        return <ClientManagement initialTab="pending" />;
      case 'services':
        return <ServiceStatus />;
      case 'users-admin':
        return <UserManagement initialTab="users" />;
      case 'users-roles':
        return <UserManagement initialTab="roles" />;
      case 'data-import':
        return <DataImportManagement />;
      case 'analytics-cache':
        return <Analytics initialTab="cache" />;
      case 'analytics-search':
        return <Analytics initialTab="search" />;
      case 'analytics-api':
        return <Analytics initialTab="api" />;
      case 'analytics-errors':
        return <Analytics initialTab="errors" />;
      case 'analytics-performance':
        return <Analytics initialTab="performance" />;
      case 'analytics-feedback':
        return <Analytics initialTab="feedback" />;
      case 'analytics-billing':
        return <Analytics initialTab="billing" />;
      case 'logs-events':
        return <Logs initialTab="event-logs" />;
      case 'logs-search-history':
        return <Logs initialTab="search-history" />;
      case 'logs-system':
        return <Logs initialTab="system-logs" />;
      default:
        return <OverviewTab />;
    }
  };

  // Build breadcrumb
  const getBreadcrumbItems = () => {
    const items = [
      { title: <HomeOutlined />, href: '/' },
      { title: 'Admin' },
    ];

    const config = pageConfigs[activePage];
    if (config?.parent === 'logs-analytics') {
      items.push({ title: 'Logs & Analytics' });
    } else if (config?.parent === 'tenants') {
      items.push({ title: 'Tenants' });
    } else if (config?.parent === 'users') {
      items.push({ title: 'Users' });
    }
    items.push({ title: currentPage?.label || 'Dashboard' });

    return items;
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Spin size="large" />
        <Text style={{ color: '#fff' }}>Loading dashboard...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#f0f2f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <Alert
          message="Access Denied"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/')}>
              Go to Home
            </Button>
          }
        />
      </div>
    );
  }

  const siderWidth = 260;
  const collapsedWidth = 80;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sider
        width={siderWidth}
        collapsedWidth={isMobile ? 0 : collapsedWidth}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, #001529 0%, #002140 100%)',
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
          zIndex: 100,
        }}
        trigger={null}
      >
        {/* Logo */}
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? '0 12px' : '0 24px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            gap: 12,
          }}
        >
          <img
            src="/images/partsgpt.png"
            alt="PartsGPT Logo"
            style={{
              width: collapsed ? 28 : 32,
              height: collapsed ? 28 : 32,
              borderRadius: 8,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          {!collapsed && (
            <div style={{ overflow: 'hidden' }}>
              <Title level={5} style={{ margin: 0, color: '#fff', whiteSpace: 'nowrap' }}>
                PartsGPT
              </Title>
              <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
                Admin Portal
              </Text>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKeys()}
          openKeys={collapsed ? [] : openKeys}
          onOpenChange={handleOpenChange}
          items={sidebarMenuItems}
          style={{
            borderRight: 0,
            marginTop: 8,
            background: 'transparent',
          }}
        />

        {/* System status at bottom of sidebar */}
        <Tooltip
          title={
            collapsed
              ? `${systemStatus.text} (${systemStatus.healthyCount}/${systemStatus.totalCount})`
              : undefined
          }
          placement="right"
        >
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              padding: collapsed ? '12px 8px' : '12px 24px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(0,0,0,0.2)',
              cursor: 'pointer',
            }}
            onClick={() => handleMenuClick('services')}
          >
            <Flex align="center" gap={8} justify={collapsed ? 'center' : 'flex-start'}>
              <Badge status={systemStatus.status} />
              {!collapsed && (
                <div style={{ overflow: 'hidden' }}>
                  <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, display: 'block' }}>
                    {systemStatus.text}
                  </Text>
                  <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>
                    {systemStatus.healthyCount}/{systemStatus.totalCount} services
                  </Text>
                </div>
              )}
            </Flex>
          </div>
        </Tooltip>
      </Sider>

      {/* Main Layout */}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? collapsedWidth : siderWidth),
          transition: 'margin-left 0.2s',
        }}
      >
        {/* Header */}
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,21,41,0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 99,
            height: 64,
          }}
        >
          <Flex align="center" gap={16}>
            {/* Collapse Toggle */}
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 18 }}
            />

            {/* Breadcrumb */}
            <Breadcrumb items={getBreadcrumbItems()} />
          </Flex>

          {/* Header Right */}
          <Flex align="center" gap={8}>
            {/* Notifications (placeholder) */}
            <Tooltip title="Notifications">
              <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} />
            </Tooltip>

            {/* User Dropdown */}
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
              <Flex
                align="center"
                gap={8}
                style={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: 8,
                  transition: 'background 0.2s',
                }}
                className="user-dropdown-trigger"
              >
                <Avatar
                  style={{ backgroundColor: '#1677ff' }}
                  icon={<UserOutlined />}
                >
                  {adminUser?.full_name?.[0] || adminUser?.username?.[0]}
                </Avatar>
                {!isMobile && (
                  <div style={{ lineHeight: 1.2 }}>
                    <Text strong style={{ display: 'block', fontSize: 13 }}>
                      {adminUser?.full_name || adminUser?.username}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: 'capitalize' }}>
                      {adminUser?.role?.replace('_', ' ')}
                    </Text>
                  </div>
                )}
              </Flex>
            </Dropdown>
          </Flex>
        </Header>

        {/* Content Area */}
        <Content
          style={{
            margin: '12px 24px',
            minHeight: 'calc(100vh - 64px - 24px)',
          }}
        >
          {/* Page Title */}
          <div style={{ marginBottom: 24 }}>
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={12}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {currentPage?.icon && React.cloneElement(currentPage.icon as React.ReactElement, {
                    style: { color: '#fff', fontSize: 20 },
                  })}
                </div>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {currentPage?.label}
                  </Title>
                  {currentPage?.description && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {currentPage.description}
                    </Text>
                  )}
                </div>
              </Flex>
              {activePage === 'overview' && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    const event = new CustomEvent('refreshOverview');
                    window.dispatchEvent(event);
                  }}
                >
                  Refresh
                </Button>
              )}
            </Flex>
          </div>

          {/* Page Content */}
          <div>{renderPageContent()}</div>
        </Content>
      </Layout>

      {/* Mobile Overlay */}
      {isMobile && !collapsed && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 99,
          }}
          onClick={() => setCollapsed(true)}
        />
      )}
    </Layout>
  );
}

// KPI Card Component
interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  loading?: boolean;
  trend?: number;
  onClick?: () => void;
  color?: 'success' | 'warning' | 'error' | 'info' | 'default';
}

function KPICard({ title, value, subtitle, icon, loading, trend, onClick, color = 'default' }: KPICardProps) {
  const getColorStyles = () => {
    switch (color) {
      case 'success':
        return { cardBg: '#fff', iconBg: 'rgba(107,175,175,0.15)', iconColor: '#5a9e9e' };
      case 'warning':
        return { cardBg: '#fff', iconBg: 'rgba(232,168,56,0.15)', iconColor: '#c4912e' };
      case 'error':
        return { cardBg: '#fff', iconBg: 'rgba(232,99,122,0.15)', iconColor: '#c4566b' };
      case 'info':
        return { cardBg: '#fff', iconBg: 'rgba(92,122,234,0.15)', iconColor: '#4a68c9' };
      default:
        return { cardBg: '#fff', iconBg: 'rgba(139,126,200,0.15)', iconColor: '#7569a8' };
    }
  };

  const colorStyles = getColorStyles();

  return (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      style={{
        height: '100%',
        borderRadius: 16,
        border: 'none',
        background: colorStyles.cardBg,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}
      styles={{ body: { padding: 20 } }}
    >
      <Flex justify="space-between" align="flex-start">
        <div style={{ flex: 1 }}>
          <Text
            type="secondary"
            style={{
              fontSize: 13,
              display: 'block',
              marginBottom: 8,
            }}
          >
            {title}
          </Text>
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 80 }} />
          ) : (
            <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Title>
          )}
          {trend !== undefined && !loading && (
            <Space size={4} style={{ marginTop: 8 }}>
              {trend >= 0 ? (
                <RiseOutlined style={{ color: '#52c41a', fontSize: 14 }} />
              ) : (
                <FallOutlined style={{ color: '#ff4d4f', fontSize: 14 }} />
              )}
              <Text style={{ color: trend >= 0 ? '#52c41a' : '#ff4d4f', fontSize: 12 }}>
                {Math.abs(trend)}%
              </Text>
            </Space>
          )}
          {subtitle && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {subtitle}
            </Text>
          )}
        </div>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: colorStyles.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon as React.ReactElement, {
            style: { color: colorStyles.iconColor, fontSize: 22 },
          })}
        </div>
      </Flex>
    </Card>
  );
}

// Overview Tab Component
function OverviewTab() {
  const [stats, setStats] = useState<any>({
    totalTenants: 0,
    pendingTenants: 0,
    activeServices: 0,
    totalServices: 0,
    systemHealth: 0,
    cacheHitRate: 0,
    totalSearches: 0,
    totalRequests: 0,
    errorRate: 0,
    avgResponseTime: 0,
    activeWorkers: 0,
    activeSessions: 0,
    topSearches: [],
    searchByType: [],
    services: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadOverviewStats();
    const interval = setInterval(loadOverviewStats, 30000);
    const handleRefresh = () => loadOverviewStats();
    window.addEventListener('refreshOverview', handleRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshOverview', handleRefresh);
    };
  }, []);

  const loadOverviewStats = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch system health
      let systemHealth = 0;
      let totalServices = 0;
      let activeServices = 0;
      let activeWorkers = 0;
      let services: any[] = [];
      try {
        const healthResponse = await adminFetch(`${getBackendUrl()}/api/admin/system/health`);
        if (healthResponse.ok) {
          const healthData = await healthResponse.json();
          systemHealth = healthData.data?.health_percentage || 0;
          totalServices = healthData.data?.total_services || 0;
          activeServices = healthData.data?.healthy_services || 0;
          services = healthData.data?.services || [];
        }
      } catch (e) {}

      // Fetch system metrics
      try {
        const metricsResponse = await adminFetch(`${getBackendUrl()}/api/admin/system/metrics`);
        if (metricsResponse.ok) {
          const metricsData = await metricsResponse.json();
          activeWorkers = metricsData.data?.active_workers || 0;
          if (metricsData.data?.total_services && !totalServices) {
            totalServices = metricsData.data.total_services;
          }
          if (metricsData.data?.healthy_services !== undefined && !activeServices) {
            activeServices = metricsData.data.healthy_services;
          }
        }
      } catch (e) {}

      // Fetch tenants count
      let totalTenants = 0;
      let pendingTenants = 0;
      try {
        const tenantsResponse = await adminFetch(`${getBackendUrl()}/api/admin/clients?limit=1`);
        if (tenantsResponse.ok) {
          const tenantsData = await tenantsResponse.json();
          totalTenants = tenantsData.data?.total || 0;
        }
      } catch (e) {}

      // Fetch pending tenants
      try {
        const pendingResponse = await adminFetch(`${getBackendUrl()}/api/tenant/pending?limit=1`);
        if (pendingResponse.ok) {
          const pendingData = await pendingResponse.json();
          pendingTenants = pendingData.data?.total || pendingData.data?.pending?.length || 0;
        }
      } catch (e) {}

      // Fetch pending actions (app/user approval workflow) and add to pending count
      try {
        const actionsResponse = await adminFetch(`${getBackendUrl()}/api/admin/pending-actions?status=pending&limit=1`);
        if (actionsResponse.ok) {
          const actionsData = await actionsResponse.json();
          pendingTenants += actionsData.total || 0;
        }
      } catch (e) {}

      // Fetch cache stats
      let cacheHitRate = 0;
      try {
        const cacheResponse = await adminFetch(`${getBackendUrl()}/api/analytics/cache-stats`);
        if (cacheResponse.ok) {
          const cacheData = await cacheResponse.json();
          cacheHitRate = cacheData.data?.hit_rate || 0;
        }
      } catch (e) {}

      // Fetch search stats
      let totalSearches = 0;
      let avgResponseTime = 0;
      try {
        const searchResponse = await adminFetch(`${getBackendUrl()}/api/analytics/stats/search`);
        if (searchResponse.ok) {
          const searchData = await searchResponse.json();
          totalSearches = searchData.data?.summary?.total_searches || 0;
          avgResponseTime = searchData.data?.summary?.avg_response_time || 0;
        }
      } catch (e) {}

      // Fetch active sessions count
      let activeSessions = 0;
      try {
        const activeRes = await adminFetch(`${getBackendUrl()}/api/analytics/stats/active-sessions`);
        if (activeRes.ok) {
          const activeData = await activeRes.json();
          activeSessions = activeData.data?.active_sessions || 0;
        }
      } catch (e) {}

      // Fetch API usage stats
      let totalRequests = 0;
      let errorRate = 0;
      try {
        const apiResponse = await adminFetch(`${getBackendUrl()}/api/analytics/stats/api`);
        if (apiResponse.ok) {
          const apiData = await apiResponse.json();
          totalRequests = apiData.data?.summary?.total_requests || 0;
          const totalErrors = apiData.data?.summary?.total_errors || 0;
          errorRate = totalRequests > 0 ? (totalErrors / totalRequests * 100) : 0;
        }
      } catch (e) {}

      // Fetch top search queries
      let topSearches: any[] = [];
      try {
        const topResponse = await adminFetch(`${getBackendUrl()}/api/analytics/stats/popular-searches?limit=10`);
        if (topResponse.ok) {
          const topData = await topResponse.json();
          topSearches = topData.data || [];
        }
      } catch (e) {}

      // Fetch search stats by type
      let searchByType: any[] = [];
      try {
        const typeResponse = await adminFetch(`${getBackendUrl()}/api/analytics/stats/search`);
        if (typeResponse.ok) {
          const typeData = await typeResponse.json();
          const statsArr = typeData.data?.stats || [];
          // Aggregate by search_type across all dates
          const typeMap: Record<string, { count: number; avgTime: number; totalTime: number }> = {};
          statsArr.forEach((s: any) => {
            const t = s.search_type || 'UNKNOWN';
            if (!typeMap[t]) typeMap[t] = { count: 0, avgTime: 0, totalTime: 0 };
            typeMap[t].count += s.total_searches || 0;
            typeMap[t].totalTime += (s.avg_response_time || 0) * (s.total_searches || 0);
          });
          searchByType = Object.entries(typeMap).map(([type, data]) => ({
            type,
            count: data.count,
            avgTime: data.count > 0 ? Math.round(data.totalTime / data.count) : 0,
          })).sort((a, b) => b.count - a.count);
        }
      } catch (e) {}

      setStats({
        totalTenants,
        pendingTenants,
        activeServices,
        totalServices,
        systemHealth: typeof systemHealth === 'number' ? parseFloat(systemHealth.toFixed(1)) : 0,
        cacheHitRate: typeof cacheHitRate === 'number' ? parseFloat(cacheHitRate.toFixed(1)) : 0,
        totalSearches,
        totalRequests,
        errorRate: typeof errorRate === 'number' ? parseFloat(errorRate.toFixed(2)) : 0,
        avgResponseTime: typeof avgResponseTime === 'number' ? Math.round(avgResponseTime) : 0,
        activeWorkers,
        activeSessions,
        topSearches,
        searchByType,
        services,
      });
    } catch (error: any) {
      setError(`Failed to load stats: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getHealthStrokeColor = (health: number): string => {
    if (health >= 80) return '#5a9e9e';
    if (health >= 50) return '#c4912e';
    return '#c4566b';
  };

  return (
    <div>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError('')}
          style={{ marginBottom: 24 }}
        />
      )}

      {/* KPI Cards Grid - Original large size */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Total Tenants"
            value={stats.totalTenants}
            subtitle="Registered tenants"
            icon={<TeamOutlined />}
            loading={loading}
            color="info"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Active Services"
            value={`${stats.activeServices}/${stats.totalServices}`}
            subtitle="Healthy services"
            icon={<CheckCircleOutlined />}
            loading={loading}
            color="success"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Total Searches"
            value={stats.totalSearches}
            subtitle="All time searches"
            icon={<SearchOutlined />}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Error Rate"
            value={`${stats.errorRate}%`}
            subtitle="Failed requests"
            icon={<ExclamationCircleOutlined />}
            loading={loading}
            color={stats.errorRate > 5 ? 'error' : stats.errorRate > 2 ? 'warning' : 'default'}
          />
        </Col>
      </Row>

      {/* Second Row - System Health (compact) + More KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* System Health - Compact Card */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              height: '100%',
              borderRadius: 16,
              border: 'none',
              background: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
            styles={{ body: { padding: 20 } }}
          >
            <Flex justify="space-between" align="flex-start">
              <div style={{ flex: 1 }}>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 13,
                    display: 'block',
                    marginBottom: 8,
                  }}
                >
                  System Health
                </Text>
                {loading ? (
                  <Skeleton.Input active size="small" style={{ width: 80 }} />
                ) : (
                  <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
                    {stats.systemHealth}%
                  </Title>
                )}
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  {loading ? '...' : `${stats.activeServices}/${stats.totalServices} services`}
                </Text>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: stats.systemHealth >= 80 ? 'rgba(107,175,175,0.18)' : stats.systemHealth >= 50 ? 'rgba(232,168,56,0.18)' : 'rgba(232,99,122,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {loading ? (
                  <Spin size="small" />
                ) : (
                  <Progress
                    type="circle"
                    percent={stats.systemHealth}
                    size={36}
                    strokeWidth={6}
                    strokeColor={getHealthStrokeColor(stats.systemHealth)}
                    format={() => null}
                  />
                )}
              </div>
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Total API Requests"
            value={stats.totalRequests}
            subtitle="All endpoints"
            icon={<BarChartOutlined />}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Avg Response Time"
            value={`${(stats.avgResponseTime / 1000).toFixed(1)}s`}
            subtitle="Average latency"
            icon={<ThunderboltOutlined />}
            loading={loading}
            color="info"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Pending Approvals"
            value={stats.pendingTenants}
            subtitle="Awaiting review"
            icon={<ClockCircleOutlined />}
            loading={loading}
            color={stats.pendingTenants > 0 ? 'warning' : 'default'}
            onClick={() => {
              const event = new CustomEvent('navigateTo', { detail: 'tenants-pending' });
              window.dispatchEvent(event);
            }}
          />
        </Col>
      </Row>

      {/* Third Row - More Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Cache Hit Rate"
            value={`${stats.cacheHitRate}%`}
            subtitle="Redis performance"
            icon={<DatabaseOutlined />}
            loading={loading}
            color="success"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Active Workers"
            value={stats.activeWorkers}
            subtitle="Celery workers"
            icon={<CloudServerOutlined />}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              height: '100%',
              borderRadius: 16,
              border: 'none',
              background: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
            styles={{ body: { padding: 20 } }}
          >
            <Flex justify="space-between" align="flex-start">
              <div style={{ flex: 1 }}>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 13,
                    display: 'block',
                    marginBottom: 8,
                  }}
                >
                  Active Now
                </Text>
                {loading ? (
                  <Skeleton.Input active size="small" style={{ width: 80 }} />
                ) : (
                  <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
                    {stats.activeSessions.toLocaleString()}
                  </Title>
                )}
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  User Sessions
                </Text>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: stats.activeSessions > 0 ? 'rgba(107,175,175,0.18)' : 'rgba(139,126,200,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: stats.activeSessions > 0 ? '#52c41a' : '#d9d9d9',
                    display: 'inline-block',
                    boxShadow: stats.activeSessions > 0 ? '0 0 10px rgba(82, 196, 26, 0.6)' : 'none',
                    animation: stats.activeSessions > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                  }}
                />
              </div>
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Success Rate"
            value={`${(100 - stats.errorRate).toFixed(1)}%`}
            subtitle={`${stats.totalRequests} total requests`}
            icon={<CheckCircleOutlined />}
            loading={loading}
            color={stats.errorRate > 5 ? 'error' : stats.errorRate > 2 ? 'warning' : 'success'}
          />
        </Col>
      </Row>

      {/* Row 4 - Top Searches + Search by Type */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Top Searches Table */}
        <Col xs={24} lg={14}>
          <Card
            title={
              <Flex align="center" gap={8}>
                <SearchOutlined style={{ color: '#1677ff' }} />
                <span>Top Searches</span>
              </Flex>
            }
            style={{
              borderRadius: 12,
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              height: '100%',
            }}
            styles={{ body: { padding: 0 } }}
          >
            {loading ? (
              <div style={{ padding: 24 }}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </div>
            ) : stats.topSearches.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <Text type="secondary">No search data yet</Text>
              </div>
            ) : (
              <div style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#8c8c8c', fontWeight: 500 }}>#</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#8c8c8c', fontWeight: 500 }}>Query</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#8c8c8c', fontWeight: 500 }}>Type</th>
                      <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, color: '#8c8c8c', fontWeight: 500 }}>Count</th>
                      <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, color: '#8c8c8c', fontWeight: 500 }}>Avg Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topSearches.slice(0, 10).map((item: any, idx: number) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #fafafa' }}>
                        <td style={{ padding: '8px 16px', fontSize: 13, color: '#8c8c8c' }}>{idx + 1}</td>
                        <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: idx < 3 ? 600 : 400, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.query || 'Unknown'}
                        </td>
                        <td style={{ padding: '8px 16px' }}>
                          <Tag color={item.search_type === 'TEXT' ? 'blue' : item.search_type === 'IMAGE' ? 'green' : 'default'} style={{ fontSize: 11 }}>
                            {item.search_type || 'N/A'}
                          </Tag>
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>{item.count || 0}</td>
                        <td style={{ padding: '8px 16px', textAlign: 'right', fontSize: 13, color: '#8c8c8c' }}>
                          {item.avg_response_time ? `${(item.avg_response_time / 1000).toFixed(1)}s` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>

        {/* Search by Type */}
        <Col xs={24} lg={10}>
          <Card
            title={
              <Flex align="center" gap={8}>
                <BarChartOutlined style={{ color: '#722ed1' }} />
                <span>Search by Type</span>
              </Flex>
            }
            style={{
              borderRadius: 12,
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              height: '100%',
            }}
            styles={{ body: { padding: 20 } }}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : stats.searchByType.length === 0 ? (
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">No search data yet</Text>
              </div>
            ) : (() => {
              const totalCount = stats.searchByType.reduce((sum: number, s: any) => sum + s.count, 0);
              const typeColors: Record<string, string> = {
                TEXT: '#1677ff',
                IMAGE: '#52c41a',
                HYBRID: '#faad14',
                ASR: '#722ed1',
              };
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {stats.searchByType.map((item: any, idx: number) => {
                    const pct = totalCount > 0 ? ((item.count / totalCount) * 100).toFixed(1) : '0';
                    const color = typeColors[item.type] || '#8c8c8c';
                    return (
                      <div key={idx}>
                        <Flex justify="space-between" style={{ marginBottom: 6 }}>
                          <Flex align="center" gap={8}>
                            <Tag color={item.type === 'TEXT' ? 'blue' : item.type === 'IMAGE' ? 'green' : item.type === 'HYBRID' ? 'gold' : 'purple'} style={{ fontSize: 11, margin: 0 }}>
                              {item.type}
                            </Tag>
                            <Text style={{ fontSize: 13 }}>{item.count.toLocaleString()} searches</Text>
                          </Flex>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {pct}% &middot; avg {(item.avgTime / 1000).toFixed(1)}s
                          </Text>
                        </Flex>
                        <div style={{ height: 6, background: '#f5f5f5', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.3s' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default AdminDashboard;
